// @ts-nocheck
import { ReactNode, Children, isValidElement, cloneElement } from 'react';
import { Box, Stack } from '@/components/ui';
import { FadeIn, SlideIn, StaggeredList, useReducedMotion } from '@/components/animations';

interface AnimatedFormWrapperProps {
  children: ReactNode;
  /** Animation style for the form */
  animation?: 'fade' | 'slide' | 'stagger' | 'cascade';
  /** Delay before animation starts (ms) */
  delay?: number;
  /** Duration of each animation (ms) */
  duration?: number;
  /** Stagger delay between items (ms) */
  staggerDelay?: number;
  /** Direction for slide animations */
  slideDirection?: 'up' | 'down' | 'left' | 'right';
  /** Gap between form elements */
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export default function AnimatedFormWrapper({
  children,
  animation = 'stagger',
  delay = 100,
  duration = 400,
  staggerDelay = 80,
  slideDirection = 'up',
  gap = 'md',
}: AnimatedFormWrapperProps) {
  const reduceMotion = useReducedMotion();

  // If user prefers reduced motion, render without animations
  if (reduceMotion) {
    return <Stack gap={gap}>{children}</Stack>;
  }

  // Simple fade animation for the entire form
  if (animation === 'fade') {
    return (
      <FadeIn delay={delay} duration={duration}>
        <Stack gap={gap}>{children}</Stack>
      </FadeIn>
    );
  }

  // Slide animation for the entire form
  if (animation === 'slide') {
    return (
      <SlideIn direction={slideDirection} delay={delay} duration={duration}>
        <Stack gap={gap}>{children}</Stack>
      </SlideIn>
    );
  }

  // Stagger animation - each child animates in sequence
  if (animation === 'stagger') {
    const childArray = Children.toArray(children);

    return (
      <Stack gap={gap}>
        {childArray.map((child, index) => (
          <SlideIn
            key={index}
            direction={slideDirection}
            delay={delay + index * staggerDelay}
            duration={duration}
          >
            {child}
          </SlideIn>
        ))}
      </Stack>
    );
  }

  // Cascade animation - alternating left/right slides
  if (animation === 'cascade') {
    const childArray = Children.toArray(children);

    return (
      <Stack gap={gap}>
        {childArray.map((child, index) => (
          <SlideIn
            key={index}
            direction={index % 2 === 0 ? 'left' : 'right'}
            delay={delay + index * staggerDelay}
            duration={duration}
          >
            {child}
          </SlideIn>
        ))}
      </Stack>
    );
  }

  return <Stack gap={gap}>{children}</Stack>;
}
